import React, { useState } from "react";
import Loader from "../../Components/Loader/Loader";
import { fetchOffices } from "../../Redux/Actions/Offices";
import { fetchVehicles } from "../../Redux/Actions/Vehicles";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import "./Vehicles.scss";

const VehicleList = ({ vehicles }) => {
  if (!vehicles.length) {
    return (
      <div className="ds-vehicles__empty">
        No vehicles found. Try changing your search filters.
      </div>
    );
  }

  return (
    <div className="ds-vehicles__results">
      {vehicles.map((vehicle) => (
        <Link
          key={vehicle.id}
          to={`/vehicle/${vehicle.id}`}
          className="ds-vehicles__card"
        >
          <div className="ds-vehicles__card-heading">
            <strong>
              {[vehicle.year, vehicle.vehicle_make, vehicle.vehicle_model]
                .filter(Boolean)
                .join(" ")}
            </strong>
            <span aria-hidden="true" className="ds-vehicles__arrow">›</span>
          </div>
          <div className="ds-vehicles__card-fields">
            <div>
              <span className="ds-vehicles__label">VIN</span>
              <span className="ds-vehicles__value ds-vehicles__vin">
                {vehicle.vin ? String(vehicle.vin).slice(-8) : "—"}
              </span>
            </div>
            <div>
              <span className="ds-vehicles__label">Plate</span>
              <span className="ds-vehicles__value">{vehicle.plate || "—"}</span>
            </div>
          </div>
          {(vehicle.exterior_color || vehicle.engine_type) && (
            <div className="ds-vehicles__specs">
              {[vehicle.exterior_color, vehicle.engine_type]
                .filter(Boolean)
                .join(" | ")}
            </div>
          )}
        </Link>
      ))}
    </div>
  );
};

export const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [offices, setOffices] = useState([]);
  const [selectedOffice, selectOffice] = useState("");
  const [vehicleMakes, setVehicleMakes] = useState([]);
  const [selectedMake, setSelectedMake] = useState("");
  const [vin, setVin] = useState("");
  const [searchParams, setSearchParams] = useState({});

  const { isLoading: officesLoading } = useQuery(
    "offices",
    () => fetchOffices(),
    {
      onSuccess: (response) => setOffices(response?.data || []),
      refetchOnWindowFocus: false,
    }
  );

  // React Query refetches automatically whenever these search parameters change.
  const { isLoading: vehiclesLoading, isFetching: vehiclesFetching, isError: vehiclesError } = useQuery(
    ["vehicleData", searchParams],
    () => fetchVehicles(searchParams),
    {
      onSuccess: (response) => {
        const data = response?.data || [];
        setVehicles(data);
        // Keep makes available even when a subsequent search narrows the results.
        setVehicleMakes((previous) => {
          const found = data.map((vehicle) => vehicle.vehicle_make).filter(Boolean);
          return Array.from(new Set([...previous, ...found])).sort();
        });
      },
      refetchOnWindowFocus: false,
    }
  );

  const handleSearch = (event) => {
    event.preventDefault();
    setSearchParams({
      office_id: selectedOffice,
      vin: vin.trim(),
      make: selectedMake,
    });
  };

  const handleClear = () => {
    selectOffice("");
    setSelectedMake("");
    setVin("");
    setSearchParams({});
  };

  const isBusy = officesLoading || vehiclesLoading || vehiclesFetching;

  return (
    <div className="ds-vehicles">
      <header className="ds-vehicles__page-heading">
        <div>
          <h1>Vehicles</h1>
        </div>
      </header>

      <section className="ds-vehicles__search-card" aria-label="Search vehicles">
        <div className="ds-vehicles__search-heading">
          <h2>Find a Vehicle</h2>
          <span>Search by VIN, office, or make</span>
        </div>
        <form onSubmit={handleSearch}>
          <div className="ds-vehicles__form-grid">
            <div className="ds-vehicles__field ds-vehicles__field--vin">
              <label htmlFor="ds-vehicle-vin">VIN</label>
              <input
                id="ds-vehicle-vin"
                type="text"
                value={vin}
                onChange={(e) => setVin(e.target.value)}
                placeholder="Enter full or partial VIN"
              />
            </div>
            <div className="ds-vehicles__field">
              <label htmlFor="ds-vehicle-office">Office</label>
              <select
                id="ds-vehicle-office"
                value={selectedOffice}
                onChange={(e) => selectOffice(e.target.value)}
              >
                <option value="">All offices</option>
                {offices.map((office) => (
                  <option key={office.id} value={office.id}>
                    {office.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="ds-vehicles__field">
              <label htmlFor="ds-vehicle-make">Make</label>
              <select
                id="ds-vehicle-make"
                value={selectedMake}
                onChange={(e) => setSelectedMake(e.target.value)}
              >
                <option value="">All makes</option>
                {vehicleMakes.map((make) => (
                  <option key={make} value={make}>
                    {make}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="ds-vehicles__actions">
            <button type="submit" className="ds-vehicles__search-button" disabled={isBusy}>
              {isBusy ? "Searching..." : "Search Vehicles"}
            </button>
            <button type="button" className="ds-vehicles__clear-button" onClick={handleClear} disabled={isBusy}>
              Clear
            </button>
          </div>
        </form>
      </section>

      <div className="ds-vehicles__results-heading">
        <h2>Vehicle Results</h2>
        {!isBusy && <span>{vehicles.length} vehicles</span>}
      </div>

      {isBusy && <Loader />}
      {vehiclesError ? (
        <div className="ds-vehicles__empty" role="alert">
          Unable to load vehicles. Please try again.
        </div>
      ) : !isBusy ? (
        <VehicleList vehicles={vehicles} />
      ) : null}
    </div>
  );
};

export default Vehicles;
